import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Store, QrCode, Package, Banknote, Sparkles, CheckCircle2, ChevronRight, ChevronLeft, ShieldCheck } from "lucide-react";
import { useAutosaveOnboarding, useOnboardingProgress } from "@/lib/onboarding-progress";
import { fmtUGX } from "@/lib/format";

type Step = { key: string; title: string; hint: string; icon: React.ComponentType<{ className?: string }> };
const STEPS: Step[] = [
  { key: "profile",  title: "Business profile", hint: "Who you are and which school you serve.", icon: Store },
  { key: "banking",  title: "Payouts & fees",   hint: "Where TriSphere Pay settles your revenue.", icon: Banknote },
  { key: "kyc",      title: "KYC documents",    hint: "Upload registration & ID for admin review.", icon: ShieldCheck },
  { key: "qr",       title: "QR checkout",      hint: "Print-ready POS QR + till code.", icon: QrCode },
  { key: "stock",    title: "Starter catalog",  hint: "Load 6–8 items so you can trade today.", icon: Package },
  { key: "launch",   title: "Submit for review", hint: "Review and submit — admin approves KYC to go live.", icon: Sparkles },
];

type Category = "canteen" | "bookshop" | "uniform" | "bus" | "clinic" | "hostel" | "events" | "general";

type Item = { name: string; price_ugx: number; quantity: number };

const CANTEEN: Item[] = [
  { name: "Chapati", price_ugx: 1000, quantity: 80 },
  { name: "Rolex (egg roll)", price_ugx: 3500, quantity: 30 },
  { name: "Mandazi", price_ugx: 800, quantity: 120 },
  { name: "Meat pie", price_ugx: 2500, quantity: 40 },
  { name: "Fresh juice 300ml", price_ugx: 2000, quantity: 60 },
  { name: "Posho & beans", price_ugx: 4500, quantity: 50 },
  { name: "Pilau & chicken", price_ugx: 8500, quantity: 25 },
  { name: "Mineral water 500ml", price_ugx: 1500, quantity: 100 },
];
const BOOKSHOP: Item[] = [
  { name: "Mathematical set", price_ugx: 18000, quantity: 25 },
  { name: "Exercise book 96pg", price_ugx: 2500, quantity: 200 },
  { name: "Pen blue (box 10)", price_ugx: 10000, quantity: 40 },
  { name: "School novel set", price_ugx: 35000, quantity: 12 },
  { name: "Scientific calculator", price_ugx: 45000, quantity: 15 },
];
const GENERIC: Item[] = [
  { name: "Item A", price_ugx: 3000, quantity: 50 },
  { name: "Item B", price_ugx: 5000, quantity: 30 },
  { name: "Item C", price_ugx: 8000, quantity: 20 },
];

function starterFor(cat: Category): Item[] {
  if (cat === "canteen") return CANTEEN;
  if (cat === "bookshop") return BOOKSHOP;
  return GENERIC;
}

export function MerchantOnboardingWizard({ ownerId, onDone }: { ownerId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const { data: existing } = useOnboardingProgress("merchant", null);
  const [hydrated, setHydrated] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    business_name: "Sunset Canteen",
    category: "canteen" as Category,
    phone: "+256 ",
    email: "",
    school_id: "",
    mm_number: "",
    settlement_bank: "Stanbic Bank Uganda",
    settlement_account: "",
    fee_rate_bps: 200,
    qr_slug: "",
    till_pin: "",
    items: [] as Item[],
    kyc: {
      registration_no: "",
      ura_tin: "",
      owner_id_no: "",
      docs: [] as Array<{ doc_type: string; file_name: string; file_size: number; mime_type: string; data_url: string }>,
    },
  });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Hydrate from saved progress
  useEffect(() => {
    if (existing && !hydrated) {
      const st = existing.state as Partial<typeof form> & { step?: number };
      setForm((f) => ({ ...f, ...st }));
      setStep(existing.current_step ?? 0);
      setHydrated(true);
    } else if (!existing && !hydrated) {
      setHydrated(true);
    }
  }, [existing, hydrated]);

  // Load schools
  const { data: schools = [] } = useQuery({
    queryKey: ["onboard-merchant-schools"],
    queryFn: async () => (await (supabase.from as unknown as (t: string) => { select: (s: string) => { order: (c: string) => Promise<{ data: Array<{ id: string; name: string; district?: string }> | null }> } })("schools_public").select("id,name,district").order("name")).data ?? [],
  });

  // Autosave
  useAutosaveOnboarding("merchant", step, STEPS.length, form as unknown as Record<string, unknown>, { enabled: hydrated });

  // Generate QR slug once we know the business
  useEffect(() => {
    if (!form.qr_slug && form.business_name) {
      const slug = form.business_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 24);
      const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
      set("qr_slug", `${slug}-${suffix}`);
      set("till_pin", Math.floor(1000 + Math.random() * 8999).toString());
    }
  }, [form.business_name, form.qr_slug]);

  // Seed starter items when entering stock step
  useEffect(() => {
    if (step === 4 && form.items.length === 0) set("items", starterFor(form.category));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const progressPct = Math.round(((step + 1) / STEPS.length) * 100);

  const stepValid = useMemo(() => {
    if (step === 0) return form.business_name.trim().length >= 2 && !!form.school_id && form.phone.length >= 6;
    if (step === 1) return !!form.settlement_bank && form.settlement_account.length >= 4;
    if (step === 2) return form.kyc.registration_no.trim().length >= 3 && form.kyc.docs.length >= 1;
    if (step === 3) return !!form.qr_slug && form.till_pin.length === 4;
    if (step === 4) return form.items.length > 0 && form.items.every((i) => i.name && i.price_ugx > 0);
    return true;
  }, [step, form]);

  const commit = useMutation({
    mutationFn: async () => {
      const { data: m, error } = await supabase.from("merchants").insert({
        owner_id: ownerId,
        name: form.business_name.trim(),
        category: form.category,
        school_id: form.school_id,
        contact_phone: form.phone,
        contact_email: form.email || null,
        fee_rate_bps: form.fee_rate_bps,
        active: false,
        lifecycle_status: "pending_review",
        setup_step: STEPS.length,
        setup_complete: true,
        qr_slug: form.qr_slug,
        qr_generated_at: new Date().toISOString(),
      } as never).select("*").single();
      if (error) throw error;
      const merchant = m as { id: string };
      if (form.items.length) {
        const payload = form.items.map((i) => ({ merchant_id: merchant.id, name: i.name, sku: i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 16), price_ugx: i.price_ugx, quantity: i.quantity, unit: "each", category: form.category, active: true }));
        await supabase.from("stock_items").insert(payload as never);
      }
      if (form.kyc.docs.length) {
        const docs = form.kyc.docs.map((d) => ({
          merchant_id: merchant.id,
          doc_type: d.doc_type,
          doc_number: d.doc_type === "registration" ? form.kyc.registration_no
            : d.doc_type === "tin" ? form.kyc.ura_tin
            : d.doc_type === "owner_id" ? form.kyc.owner_id_no : null,
          storage_path: d.data_url,
          file_name: d.file_name,
          file_size: d.file_size,
          mime_type: d.mime_type,
          status: "submitted",
        }));
        await supabase.from("merchant_documents" as never).insert(docs as never);
      }
      // mark onboarding complete
      await (supabase.rpc as unknown as (fn: string, a: Record<string, unknown>) => Promise<unknown>)("mark_onboarding_step", {
        _flow: "merchant", _step: STEPS.length, _total: STEPS.length, _state: form,
        _entity: merchant.id, _completed: true,
      });
      return merchant;
    },
    onSuccess: () => {
      toast.success("Submitted for KYC review — school admin will approve shortly");
      qc.invalidateQueries();
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const StepIcon = STEPS[step].icon;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Card className="p-6 bg-gradient-to-br from-primary/10 via-card to-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Merchant onboarding</Badge>
            <h1 className="mt-2 font-display text-3xl">Open your TriSphere till</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Step {step + 1} of {STEPS.length}: {STEPS[step].hint}
              {existing && !existing.completed && step > 0 && <span className="ml-1 italic">· resumed</span>}
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-muted-foreground">Progress</div>
            <div className="font-display text-2xl">{progressPct}%</div>
          </div>
        </div>
        <Progress value={progressPct} className="mt-4 h-2" />
        <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-1 text-[10px]">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`flex items-center gap-1 rounded px-2 py-1 ${i <= step ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
              {i < step ? <CheckCircle2 className="h-3 w-3" /> : <s.icon className="h-3 w-3" />}
              <span className="truncate">{s.title}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div className="flex items-center gap-2">
          <StepIcon className="h-5 w-5 text-primary" />
          <h2 className="font-display text-2xl">{STEPS[step].title}</h2>
        </div>

        {step === 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5"><Label>Business name</Label><Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v as Category)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(["canteen","bookshop","uniform","bus","clinic","hostel","events","general"] as Category[]).map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>School you serve</Label>
              <Select value={form.school_id} onValueChange={(v) => set("school_id", v)}>
                <SelectTrigger><SelectValue placeholder="Pick a school" /></SelectTrigger>
                <SelectContent>{schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.district ? ` · ${s.district}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Phone (MoMo)</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+256 7xx xxx xxx" /></div>
            <div className="space-y-1.5"><Label>Email (optional)</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ops@business.com" /></div>
          </div>
        )}

        {step === 1 && (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Settlement bank</Label>
              <Select value={form.settlement_bank} onValueChange={(v) => set("settlement_bank", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Stanbic Bank Uganda","Centenary Bank","Equity Bank","DFCU Bank","Absa Bank Uganda","Standard Chartered"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Settlement account</Label><Input value={form.settlement_account} onChange={(e) => set("settlement_account", e.target.value)} placeholder="9030000000" /></div>
            <div className="space-y-1.5"><Label>MoMo till (optional)</Label><Input value={form.mm_number} onChange={(e) => set("mm_number", e.target.value)} placeholder="+256 7xx xxx xxx" /></div>
            <div className="space-y-1.5">
              <Label>TriSphere Pay fee</Label>
              <Select value={String(form.fee_rate_bps)} onValueChange={(v) => set("fee_rate_bps", Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="100">1.00% · bus/transport</SelectItem>
                  <SelectItem value="200">2.00% · canteen/bookshop</SelectItem>
                  <SelectItem value="250">2.50% · general merchant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Card className="p-3 md:col-span-2 bg-muted/30">
              <p className="text-xs text-muted-foreground">Money movement: Parent wallet → TriSphere Pay pooled account → T+1 batch settlement to your bank/MoMo. Every transaction is journaled to your ledger for audit.</p>
            </Card>
          </div>
        )}

        {step === 2 && (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5"><Label>Ministry / Registration no.</Label><Input value={form.kyc.registration_no} onChange={(e) => set("kyc", { ...form.kyc, registration_no: e.target.value })} placeholder="MoTIC/00123" /></div>
            <div className="space-y-1.5"><Label>URA TIN</Label><Input value={form.kyc.ura_tin} onChange={(e) => set("kyc", { ...form.kyc, ura_tin: e.target.value })} placeholder="1000-000-000" /></div>
            <div className="space-y-1.5"><Label>Owner National ID</Label><Input value={form.kyc.owner_id_no} onChange={(e) => set("kyc", { ...form.kyc, owner_id_no: e.target.value })} placeholder="CM1234..." /></div>
            <div className="space-y-1.5">
              <Label>Upload documents (registration, TIN, ID)</Label>
              <Input type="file" accept="image/*,application/pdf" multiple onChange={async (e) => {
                const files = Array.from(e.target.files ?? []);
                const docs = await Promise.all(files.map((f, i) => new Promise<typeof form.kyc.docs[number]>((resolve) => {
                  const r = new FileReader();
                  r.onload = () => resolve({
                    doc_type: ["registration","tin","owner_id"][i] ?? "other",
                    file_name: f.name, file_size: f.size, mime_type: f.type,
                    data_url: String(r.result ?? ""),
                  });
                  r.readAsDataURL(f);
                })));
                set("kyc", { ...form.kyc, docs: [...form.kyc.docs, ...docs] });
              }} />
              <p className="text-[11px] text-muted-foreground">Files are stored securely and only visible to school admins & reviewers.</p>
            </div>
            <div className="md:col-span-2">
              {form.kyc.docs.length > 0 && (
                <div className="rounded-lg border divide-y">
                  {form.kyc.docs.map((d, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 min-w-0"><ShieldCheck className="h-4 w-4 text-primary shrink-0" />
                        <span className="truncate">{d.file_name}</span>
                        <Badge variant="outline" className="text-[10px]">{d.doc_type}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground font-mono">{(d.file_size/1024).toFixed(0)}KB</span>
                        <Button size="sm" variant="ghost" onClick={() => set("kyc", { ...form.kyc, docs: form.kyc.docs.filter((_, j) => j!==i) })}>×</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Card className="p-3 md:col-span-2 bg-amber-50/40 border-amber-200/50">
              <p className="text-xs text-amber-900">Your till will remain <b>inactive</b> until a school admin reviews and approves your KYC. You'll be notified in-app.</p>
            </Card>
          </div>
        )}

        {step === 3 && (
          <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
            <Card className="p-6 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-primary/5 to-card">
              <div className="grid h-40 w-40 place-items-center rounded-xl bg-foreground text-background">
                <QrCode className="h-24 w-24" />
              </div>
              <div className="text-center">
                <div className="text-xs uppercase text-muted-foreground tracking-wider">Merchant QR slug</div>
                <div className="font-mono text-sm mt-0.5 break-all">{form.qr_slug}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => {
                const slug = form.business_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 20) + "-" + Math.random().toString(36).slice(2,6).toUpperCase();
                set("qr_slug", slug);
              }}>Regenerate</Button>
            </Card>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Till PIN (4 digits)</Label>
                <Input value={form.till_pin} onChange={(e) => set("till_pin", e.target.value.replace(/[^0-9]/g,"").slice(0,4))} placeholder="1234" />
                <p className="text-[11px] text-muted-foreground">Cashiers unlock the POS with this PIN. Rotate anytime from Settings.</p>
              </div>
              <Card className="p-3 space-y-2 bg-muted/30">
                <div className="text-xs font-semibold">Payer options unlocked</div>
                {[
                  { k: "Student card tap · NFC/QR", ok: true },
                  { k: "Parent wallet push (in-app)", ok: true },
                  { k: "MoMo pay-to-till", ok: true },
                  { k: "Bank card (Visa/Mastercard)", ok: false },
                ].map((r) => (
                  <div key={r.k} className="flex items-center justify-between text-xs">
                    <span>{r.k}</span>
                    <Badge variant={r.ok ? "default" : "secondary"} className="h-5 text-[10px]">{r.ok ? "on" : "coming"}</Badge>
                  </div>
                ))}
              </Card>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">Edit prices, quantities, or add rows. You can bulk-import later.</div>
              <Button size="sm" variant="outline" onClick={() => set("items", [...form.items, { name: "", price_ugx: 0, quantity: 0 }])}>Add row</Button>
            </div>
            <div className="overflow-hidden rounded-lg border">
              <div className="grid grid-cols-[1fr_120px_100px_60px] gap-2 border-b bg-muted/40 px-3 py-2 text-[11px] uppercase text-muted-foreground">
                <div>Item</div><div>Price (UGX)</div><div>Qty</div><div></div>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_120px_100px_60px] gap-2 border-b px-3 py-2 items-center">
                  <Input value={it.name} onChange={(e) => set("items", form.items.map((x, j) => j===i ? { ...x, name: e.target.value } : x))} />
                  <Input type="number" value={it.price_ugx} onChange={(e) => set("items", form.items.map((x, j) => j===i ? { ...x, price_ugx: Number(e.target.value) } : x))} />
                  <Input type="number" value={it.quantity} onChange={(e) => set("items", form.items.map((x, j) => j===i ? { ...x, quantity: Number(e.target.value) } : x))} />
                  <Button size="sm" variant="ghost" onClick={() => set("items", form.items.filter((_, j) => j!==i))}>×</Button>
                </div>
              ))}
            </div>
            <div className="text-right text-xs text-muted-foreground">
              Est. opening inventory value: <span className="font-mono">{fmtUGX(form.items.reduce((a, x) => a + x.price_ugx * x.quantity, 0))}</span>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="p-4 space-y-1.5">
              <div className="text-xs uppercase text-muted-foreground">Business</div>
              <div className="font-display text-xl">{form.business_name}</div>
              <div className="text-sm capitalize text-muted-foreground">{form.category}</div>
              <div className="mt-2 text-xs">Phone {form.phone} · {form.email || "no email"}</div>
            </Card>
            <Card className="p-4 space-y-1.5">
              <div className="text-xs uppercase text-muted-foreground">Payouts</div>
              <div className="text-sm">{form.settlement_bank}</div>
              <div className="font-mono text-sm">{form.settlement_account}</div>
              <div className="mt-2 text-xs">Fee {(form.fee_rate_bps/100).toFixed(2)}% · T+1 settlement</div>
            </Card>
            <Card className="p-4 space-y-1.5">
              <div className="text-xs uppercase text-muted-foreground">Till</div>
              <div className="font-mono text-sm break-all">{form.qr_slug}</div>
              <div className="text-xs">PIN ****</div>
            </Card>
            <Card className="p-4 space-y-1.5">
              <div className="text-xs uppercase text-muted-foreground">Catalog</div>
              <div className="text-2xl font-display">{form.items.length} items</div>
              <div className="text-xs">{fmtUGX(form.items.reduce((a,x) => a + x.price_ugx * x.quantity, 0))} opening value</div>
            </Card>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            <ChevronLeft className="mr-1 h-4 w-4" />Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button disabled={!stepValid} onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>
              Continue<ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          ) : (
            <Button disabled={commit.isPending} onClick={() => commit.mutate()}>
              {commit.isPending ? "Submitting…" : "Submit for KYC review"}
              <Sparkles className="ml-1 h-4 w-4" />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
